import { useEffect, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import InputAdornment from '@mui/material/InputAdornment';
import { filterInKeyValue, findByValue } from '@witmetrics/utils/arrays';
import { currentDate, formatDateWithTime } from '@witmetrics/utils/time';
import { isBlank } from '@witmetrics/utils/strings';
import { useToggle } from '@/hooks/useToggle';
import { useArray } from '@/hooks/useArray';
import { useStringId } from '@/hooks/useId';
import { useMountEffect } from '@/hooks/useMountEffect';
import { useString } from '@/hooks/useString';
import Dialog, { DialogBody, DialogTitle } from '@/components/Dialog';
import Button from '@/components/Button';
import SelectDropdown from '@/components/inputs/SelectDropdown';
import { CameraIcon, SmileIcon, UploadIcon } from '@/icons';
import LoopingEllipsis from '@/components/LoopingEllipsis';
import TextField from '@/components/inputs/TextField';
import { blobToFile, buildConstraints, classes, renamePhoto } from './utils';

export type PhotoCaptureDialogProps = {
  onStartSimulation: (file: File) => void;
  onSave: (file: File) => void;
  onClose: () => void;
};

export default function PhotoCaptureDialog({
  onStartSimulation,
  onSave,
  onClose,
}: PhotoCaptureDialogProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [devices, setDevices] = useArray<MediaDeviceInfo>();
  const [activeDeviceId, setActiveDeviceId] = useStringId();
  const [name, setName] = useString();
  const [photo, setPhoto] = useState<File | null>(null);
  const [isLoading, toggleLoading] = useToggle();

  useMountEffect(() => {
    return () => {
      // Clean up the stream when the component unmounts
      if (streamRef.current) {
        const tracks = streamRef.current.getTracks();
        tracks.forEach((track) => track.stop());
        streamRef.current = null;
      }
    };
  });

  useEffect(() => {
    // Basically just giving the videoRef time to load
    if (!isLoading && !activeDeviceId) toggleLoading(true);
    else if (isLoading && !activeDeviceId) requestCameraAccess();
  }, [activeDeviceId, isLoading]);

  const requestCameraAccess = async () => {
    toggleLoading(true);
    if (!videoRef.current) return;
    const stream =
      await navigator.mediaDevices.getUserMedia(buildConstraints());
    const deviceId = stream.getVideoTracks()[0].getSettings().deviceId;
    setActiveDeviceId(deviceId);
    setDevices(
      filterInKeyValue(
        await navigator.mediaDevices.enumerateDevices(),
        'kind',
        'videoinput'
      )
    );
    streamRef.current = stream;
    videoRef.current.srcObject = streamRef.current;
    videoRef.current.play();
    toggleLoading(false);
  };

  const handleSelectDevice = async (deviceId: string) => {
    if (!videoRef.current) return;
    if (streamRef.current) {
      const tracks = streamRef.current.getTracks();
      tracks.forEach((track) => track.stop());
      streamRef.current = null;
    }
    const stream = await navigator.mediaDevices.getUserMedia(
      buildConstraints(deviceId)
    );
    setActiveDeviceId(deviceId);
    streamRef.current = stream;
    videoRef.current.srcObject = streamRef.current;
    videoRef.current.play();
  };

  const handleCapture = async () => {
    if (!videoRef.current) return;
    const canvas = document.createElement('canvas');
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    const context = canvas.getContext('2d');
    if (context) {
      context.drawImage(videoRef.current, 0, 0);
      canvas.toBlob((blob) => {
        if (blob) {
          const timestamp = formatDateWithTime(currentDate());
          const filename = `Photo ${timestamp}`;
          const file = blobToFile(blob, filename);
          setName(filename);
          setPhoto(file);
        }
      }, 'image/png');
    }
  };

  const renderFooter = () => {
    if (isLoading) return null;
    const disabled = isBlank(name);
    if (photo) {
      return (
        <div className={classes.footer}>
          <TextField
            autoFocus
            error={disabled}
            className={classes.nameField}
            label="Name"
            value={name}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">.png</InputAdornment>
              ),
            }}
            onFocus={(e) => e.target.select()}
            onChange={setName}
          />
          <div className={classes.buttonsWrapper}>
            <Button onClick={() => setPhoto(null)}>
              <CameraIcon className={classes.buttonIcon} />
              Retake
            </Button>
            <Button
              disabled={disabled}
              onClick={() => onSave(renamePhoto(photo, name))}>
              <UploadIcon className={classes.buttonIcon} />
              Save Image
            </Button>
            <Button
              disabled={disabled}
              onClick={() => onStartSimulation(renamePhoto(photo, name))}>
              <SmileIcon className={classes.buttonIcon} />
              Smile Simulation
            </Button>
          </div>
        </div>
      );
    }
    return (
      <div className={classes.footer}>
        <SelectDropdown
          className={classes.deviceField}
          value={activeDeviceId ? findByValue(options, activeDeviceId) : null}
          options={options}
          onChange={({ value }) => handleSelectDevice(value)}
        />
        <Button disabled={!activeDeviceId} onClick={handleCapture}>
          <CameraIcon className={classes.buttonIcon} />
          Capture
        </Button>
      </div>
    );
  };

  const options = devices.map((d) => ({
    value: d.deviceId,
    label: d.label,
  }));

  return (
    <Dialog className={classes.dialog} onClose={onClose}>
      <DialogTitle
        className={classes.title}
        title="Take a Picture"
        onClose={onClose}
      />
      <DialogBody className={classes.body}>
        <video
          ref={videoRef}
          className={twMerge(classes.video, photo && classes.hidden)}
        />
        {photo && (
          <div className={classes.photoWrapper}>
            <img alt="photo" src={URL.createObjectURL(photo)} />
          </div>
        )}
        {isLoading && (
          <div className={classes.loading}>
            <div>
              Starting camera
              <LoopingEllipsis />
            </div>
          </div>
        )}
        <div className={classes.footerWrapper}>{renderFooter()}</div>
      </DialogBody>
    </Dialog>
  );
}
